import React from 'react';

import { getBillingFormattedDate, getFormattedDate, parseToDate } from '../../../../utils/date';
import disengagedDictionary from '../../DisengagedMembers/disengagedDictionary';
import { MembershipLog, OrganizationMemberDetails } from '../../types';

export type PreparedMembershipLog = {
    title: string;
    description?: string;
    timestamp?: string | null;
};

export type MembershipLogGroup = {
    items: PreparedMembershipLog[];
    // Length of the repeating unit (1 = same event repeated, 2+ = a repeating
    // cycle such as Disengaged → Re-engaged). 1 for non-repeating single rows.
    unitLength: number;
    // How many times the unit repeats consecutively (>= 2 means collapsible).
    repeatCount: number;
};

const MAX_UNIT_LENGTH = 4;

const logKey = (log: PreparedMembershipLog): string => `${log.title}|${log.description ?? ''}`;

// Number of times the block [start, start + unit) repeats consecutively (>= 1).
const countUnitRepeats = (keys: string[], start: number, unit: number): number => {
    let repeat = 1;

    while (start + unit * (repeat + 1) <= keys.length) {
        let matches = true;

        for (let offset = 0; offset < unit; offset++) {
            if (keys[start + offset] !== keys[start + unit * repeat + offset]) {
                matches = false;
                break;
            }
        }

        if (!matches) {
            break;
        }

        repeat++;
    }

    return repeat;
};

// Collapses continuous 2+ repeats into a single group. A "repeat" is a repeating
// unit of one event (e.g. the same reminder N times) or a repeating cycle of
// several events (e.g. Disengaged → Re-engaged occurring every day). The unit
// that yields the largest coverage wins, preferring the shortest unit on a tie.
export const groupMembershipLogs = (logs: PreparedMembershipLog[]): MembershipLogGroup[] => {
    const keys = logs.map(logKey);
    const groups: MembershipLogGroup[] = [];
    let index = 0;

    while (index < logs.length) {
        let bestUnit = 1;
        let bestRepeat = 1;

        for (let unit = 1; unit <= MAX_UNIT_LENGTH && index + unit * 2 <= logs.length; unit++) {
            const repeat = countUnitRepeats(keys, index, unit);
            if (repeat >= 2 && repeat * unit > bestRepeat * bestUnit) {
                bestUnit = unit;
                bestRepeat = repeat;
            }
        }

        const isRepeating = bestRepeat >= 2;
        const count = isRepeating ? bestUnit * bestRepeat : 1;

        groups.push({
            items: logs.slice(index, index + count),
            unitLength: isRepeating ? bestUnit : 1,
            repeatCount: isRepeating ? bestRepeat : 1
        });

        index += count;
    }

    return groups;
};

const getMembershipTimestamp = (title: MembershipLog['event'], timestamp: string) => {
    if (!timestamp) {
        return 'N/A';
    }

    if (title === 'activated' || title === 'discharged' || title === 'cancelled') {
        return getBillingFormattedDate(timestamp, 'MMM d, yyyy');
    }

    return getFormattedDate(parseToDate(timestamp), 'MMM d, yyyy');
};

const getMembershipEventTitle = (title: MembershipLog['event']): string => {
    switch (title) {
        case 'vacation_started':
        case 'acknowledged':
            return 'Away from home start';
        case 'vacation_cancelled':
        case 'vacation_ended':
            return 'Away from home end';
        case 'activated':
            return 'Activated';
        case 'disengagement':
            return 'Disengaged';
        case 're_engagement':
            return 'Re-engaged';
        case 'monthly_call_reminder':
            return 'Monthly call reminder sent';
        case 'cancelled':
            return 'Discharge finalized';
        case 'discharged':
            return 'Discharged';
        case 'readmitted':
            return 'Re-admitted';
        default:
            return title;
    }
};

const processMembershipLog = (log: MembershipLog): PreparedMembershipLog[] => {
    const output: PreparedMembershipLog[] = [];

    if (!log.timestamp) {
        return output;
    }

    if (log.event === 'undefined') {
        return output;
    }

    if (log.event === 'vacation_cancelled') {
        if (log.user_end_datetime) {
            output.push({
                title: 'Away from home end',
                timestamp: getFormattedDate(parseToDate(log.user_end_datetime), 'MMM d, yyyy')
            });
        }
        if (log.user_start_datetime) {
            output.push({
                title: 'Away from home start',
                timestamp: getFormattedDate(parseToDate(log.user_start_datetime), 'MMM d, yyyy')
            });
        }
        return output;
    }

    if (log.event === 'discharged' && log.readmitted_at) {
        output.push({
            title: 'Re-admitted',
            timestamp: getFormattedDate(parseToDate(log.readmitted_at), 'MMM d, yyyy')
        });
    }

    output.push({
        title: getMembershipEventTitle(log.event),
        timestamp: getMembershipTimestamp(log.event, log.timestamp),
        description: log.disengaged_reason
            ? disengagedDictionary[log.disengaged_reason]?.label
            : log.event === 'vacation_started' && log.user_end_datetime
              ? `End scheduled for ${getFormattedDate(
                    parseToDate(log.user_end_datetime),
                    'MMM d, yyyy'
                )}`
              : log.discharged_reason
                ? log.discharged_reason
                : undefined
    });

    return output;
};

const sortMembershipLogs = (logs: PreparedMembershipLog[]): PreparedMembershipLog[] => {
    return logs.sort((a, b) => {
        if (!a.timestamp || !b.timestamp) return 0;

        const dateA = new Date(a.timestamp).getTime();
        const dateB = new Date(b.timestamp).getTime();
        return dateB - dateA;
    });
};

export const usePreparedMembershipLogs = (
    memberDetails: OrganizationMemberDetails | null
): PreparedMembershipLog[] => {
    return React.useMemo(() => {
        if (!memberDetails?.member_info?.membership_logs) {
            return [];
        }

        const output: PreparedMembershipLog[] = [];

        for (const log of memberDetails.member_info.membership_logs) {
            const processedLogs = processMembershipLog(log);
            output.push(...processedLogs);
        }

        return sortMembershipLogs(output);
    }, [memberDetails]);
};
